// BL-177 Step 3 (B4): the "Scheduled" view of Communications. Staff see their own scheduled messages; company admins and platform admins
// see everyone's in their company and can cancel any of them. Messages stopped with a reason (failed, cancelled by the system) stay
// listed for a few days so the reason is seen.
import React, { useState } from 'react';
import Button3D from '../ui/Button3D';
import StatusChip from '../ui/StatusChip';
import { canManageScheduled, describeScheduledStatus } from '../../lib/scheduledSend';
import { formatPhone } from '../../lib/phone';
import { threadDisplayName } from '../../lib/communicationsDisplay';

const CHANNEL_WORDS = { sms: 'Text', mms: 'Picture message', whatsapp: 'WhatsApp', email: 'Email' };

function recipientName(row, threads) {
    const thread = row.session_id ? threads.find((entry) => entry.id === row.session_id || entry.session?.id === row.session_id) : null;
    if (thread) return threadDisplayName(thread) || 'Conversation';
    if (row.group_addresses?.length) return `Group text · ${row.group_addresses.length} people`;
    const address = row.recipient_address || '';
    return address.includes('@') ? address : (formatPhone(address) || address || 'Conversation');
}

// BL-177 Step 2j: the admin "Delivery hours" setting is gone. A scheduled staff message goes out at the time it was scheduled for; quiet hours
// will apply only to Campaigns / marketing in the future SEO / Marketing module. (The org_scheduled_send_settings table is kept.)

export default function ScheduledList({ rows = [], threads = [], staffNames = {}, userId = null, isAdmin = false, onOpen, onCancel, error = '' }) {
    const [busyId, setBusyId] = useState('');
    const [rowErrors, setRowErrors] = useState({});
    const live = rows.filter((row) => row.status === 'scheduled' || row.status === 'sending');
    const stopped = rows.filter((row) => row.status === 'failed' || row.status === 'cancelled');
    const cancel = async (row) => {
        if (busyId) return;
        setBusyId(row.id);
        setRowErrors((current) => ({ ...current, [row.id]: '' }));
        try { await onCancel?.(row); } catch (failure) { setRowErrors((current) => ({ ...current, [row.id]: failure?.message || 'That could not be cancelled.' })); } finally { setBusyId(''); }
    };
    const renderRow = (row) => {
        const zone = row.company_time_zone || 'America/New_York';
        const status = describeScheduledStatus(row, zone);
        const mine = row.created_by === userId;
        const who = mine ? 'you' : (staffNames[row.created_by] || 'a teammate');
        const open = row.status === 'scheduled';
        return (
            <li key={row.id} className="border-b border-[#26365A] px-3 py-3" data-scheduled-row={row.id} data-scheduled-status={row.status}>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="min-w-0 truncate text-sm font-bold text-white">{recipientName(row, threads)}</span>
                    <StatusChip tone={row.status === 'failed' ? 'red' : row.status === 'cancelled' ? 'grey' : 'purple'}>
                        {row.status === 'failed' ? 'Not sent' : row.status === 'cancelled' ? 'Cancelled' : row.waiting_until ? 'Waiting' : 'Scheduled'}
                    </StatusChip>
                </div>
                <p className="mt-0.5 text-xs text-white">{CHANNEL_WORDS[row.channel] || row.channel} · {status} · by {who}</p>
                <p className="mt-1 line-clamp-2 break-words text-sm text-white" data-scheduled-preview="true">{row.body || (row.content_sid ? 'Message template' : '')}</p>
                {rowErrors[row.id] && <p role="alert" className="mt-1 text-xs text-[#FCA5A5]">{rowErrors[row.id]}</p>}
                <div className="mt-2 flex flex-wrap gap-2">
                    {row.session_id && <Button3D variant="light" className="ia-comms-pill" onClick={() => onOpen?.(row)}>Open conversation</Button3D>}
                    {open && canManageScheduled(row, { userId, isAdmin }) && (
                        <Button3D variant="light" className="ia-comms-pill" disabled={Boolean(busyId)} onClick={() => cancel(row)} aria-label={`Cancel scheduled message to ${recipientName(row, threads)}`}>
                            {busyId === row.id ? 'Cancelling…' : 'Cancel'}
                        </Button3D>
                    )}
                </div>
            </li>
        );
    };
    if (error) return <div className="p-6 text-center text-sm text-[#FCA5A5]" role="alert">Could not load scheduled messages: {error}</div>;
    if (!rows.length) {
        return (
            <div data-scheduled-list="true">
                <div className="p-8 text-center text-sm text-white" data-scheduled-empty="true">Nothing is scheduled. Use the ▾ next to Send to schedule a message.</div>
            </div>
        );
    }
    return (
        <div data-scheduled-list="true">
            {isAdmin && <p className="px-3 pt-2 text-xs text-white">You can see and cancel everyone&apos;s scheduled messages in your company.</p>}
            <ul aria-label="Scheduled messages">{live.map(renderRow)}</ul>
            {stopped.length > 0 && (
                <>
                    <h3 className="px-3 pt-3 text-xs font-bold uppercase text-white">Stopped</h3>
                    <ul aria-label="Scheduled messages that did not go out">{stopped.map(renderRow)}</ul>
                </>
            )}
        </div>
    );
}
